import { ChevronDown, X } from 'lucide-react';
import React from 'react';

import { FilterOption } from '../types';

interface FilterDropdownButtonProps {
  name: string;
  placeholder: string;
  open: boolean;
  openUp: () => void;
  count: number;
  clearFilter: () => void;
  labels: string[];
  showLabel?: boolean;
  setSelected?: (selected: FilterOption[]) => void;
  selected?: FilterOption[];
  customPlaceholder?: React.ReactNode;
}

export const FilterDropdownButton = React.forwardRef<
  HTMLDivElement,
  FilterDropdownButtonProps
>(
  (
    {
      name,
      placeholder,
      open,
      openUp,
      count = 0,
      clearFilter,
      labels,
      showLabel,
      setSelected,
      selected,
      customPlaceholder,
    },
    ref,
  ) => {
    const updateSelectedLabels = (label: string) => {
      if (setSelected && selected) {
        setSelected(
          selected.filter((item: FilterOption) => item.label !== label),
        );
      }
    };

    const openpill = showLabel ? (
      <div className="flex max-w-full flex-wrap items-center gap-1">
        {labels.map(label => (
          <div
            key={label}
            className="max-w-50 flex min-w-0 items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
          >
            <span className="min-w-0 truncate" title={label}>
              {label}
            </span>
            <button
              onClick={() => updateSelectedLabels(label)}
              type="button"
              className="flex-shrink-0 rounded-full p-0.5 hover:bg-blue-100"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    ) : open ? (
      <div className="flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
        {placeholder}
      </div>
    ) : null;

    const pill =
      count > 0 ? (
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {openpill}
          <button
            type="button"
            className="min-h-8 min-w-10 flex-1"
            onClick={openUp}
          />
        </div>
      ) : (
        <button type="button" className="flex-1 text-left" onClick={openUp}>
          {name === 'custom' ? (
            <>{customPlaceholder}</>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </button>
      );

    const cleanAllButton = showLabel && count > 0 && (
      <button
        onClick={clearFilter}
        className="absolute right-8 top-1/2 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full bg-gray-200 text-gray-500 hover:bg-gray-300 hover:text-gray-700"
        aria-label={`Clear ${name} filter`}
        type="button"
      >
        <X className="h-3 w-3" />
      </button>
    );

    return (
      <div
        ref={ref}
        className={`
          relative flex w-full items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-left hover:bg-gray-50 focus:border-blue-500 focus:outline-none focus:ring-2
          focus:ring-blue-500
          ${open ? 'border-blue-500 ring-2 ring-blue-500' : ''}
        `}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">{pill}</div>
        <div className="flex flex-shrink-0 items-center gap-1">
          <button
            onClick={openUp}
            className="cursor-pointer"
            type="button"
            aria-label={`Toggle ${name} dropdown`}
          >
            <ChevronDown
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                open ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>
        {cleanAllButton}
      </div>
    );
  },
);
